import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';

import { TooltipPopup } from '../Tooltip';
import Button from '../Button';
import { IconHelp, IconInfo } from '../icons/Icons';

import announce from '../../../helpers/announce';

const HELP_TIP_ID = 'sarsa-help-tip';

export default class HelpTip extends PureComponent {
    static propTypes = {
        /** A single word or short phrase that describes what the help tip is for.
         * This is used as the accessible button title like this: "Help tip for
         * ${tipForDescriptor}". This must be unique to the page */
        tipForDescriptor: PropTypes.string.isRequired,

        /** The content to display inside the popup */
        content: PropTypes.node.isRequired,

        /** The maximum width of the popup, in pixels */
        maxWidth: PropTypes.number,

        /** Sets the visual appearance */
        appearance: PropTypes.oneOf(['help', 'info']),

        /** Allow setting a custom icon instead of the one based on "appearance" */
        customIcon: PropTypes.node
    };

    static defaultProps = {
        appearance: 'help'
    };

    constructor(props) {
        super(props);
        this.triggerRef = React.createRef();

        this.state = {
            isVisible: false
        };
    }

    handleClick = () => {
        if (this.state.isVisible) {
            this.hideHelpTip();
        }
        else {
            this.showHelpTip();

            // When focus remains on the tooltip and the button is clicked off
            // and on, the aria-describedby is not announced again, so we force
            // the SR to read the contents again
            announce(this.props.content);
        }
    }

    showHelpTip = () => {
        this.setState({
            isVisible: true
        });
    }

    hideHelpTip = () => {
        this.setState({
            isVisible: false
        });
    }

    render() {
        const { content, maxWidth, tipForDescriptor, appearance } = this.props;
        const { isVisible } = this.state;

        let iconBeforeElement;
        if (this.props.customIcon !== undefined) {
            iconBeforeElement = this.props.customIcon;
        }
        else {
            iconBeforeElement = appearance === 'info' ? <IconInfo /> : <IconHelp />;
        }

        return (
            <Fragment>
                <Button
                    innerRef={this.triggerRef}
                    size="sm"
                    appearance="subtle"
                    className="sarsa-help-tip"
                    iconBeforeElement={iconBeforeElement}
                    screenReaderTextBefore={`Help tip for ${tipForDescriptor}`}
                    aria-expanded={isVisible}
                    aria-describedby={isVisible ? HELP_TIP_ID : null}
                    onMouseEnter={this.showHelpTip}
                    onMouseLeave={this.hideHelpTip}
                    onFocus={this.showHelpTip}
                    onBlur={this.hideHelpTip}
                    onClick={this.handleClick} />
                <TooltipPopup
                    id={HELP_TIP_ID}
                    content={content}
                    maxWidth={maxWidth}
                    isVisible={isVisible}
                    triggerRef={this.triggerRef} />
            </Fragment>

        );
    }
}
